import React, { useRef, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";

import { Dialog } from "components";
import { useUploadBillSupportingDocumentsBulk } from "hooks";

interface BulkAttachDocumentsModalProps {
  isOpen: boolean;
  selectedBillIds: string[];
  onClose: () => void;
}

export const BulkAttachDocumentsModal: React.FC<
  BulkAttachDocumentsModalProps
> = ({ isOpen, selectedBillIds, onClose }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutateAsync, isLoading } = useUploadBillSupportingDocumentsBulk();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
  };

  const handleSubmit = async () => {
    if (!selectedFile) return;
    try {
      await mutateAsync({ billIds: selectedBillIds, file: selectedFile });
      onClose();
    } catch {
      // Errors are notified by the hook
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    setSelectedFile(null);
    onClose();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title="Attach Supporting Document"
      actions={
        <>
          <Button variant="outlined" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!selectedFile || isLoading}
            startIcon={
              isLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {isLoading ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        {`This document will be attached to ${
          selectedBillIds.length
        } selected bill ${selectedBillIds.length === 1 ? "entry" : "entries"}.`}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        <Button
          variant="outlined"
          onClick={() => fileInputRef.current?.click()}
          disabled={isLoading}
          sx={{ textTransform: "none" }}
        >
          {selectedFile ? selectedFile.name : "Choose file…"}
        </Button>
      </Box>
    </Dialog>
  );
};
